import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants/common';
import { DateRangeSelection } from '@components';
import { Loader } from '@base-app';
import { Section, SubPage } from '@builder';
import { useQueryCDCTier1ResultsBreakdown } from '@modules/study-insights/hooks';
import { getFirstDayOfMonth, getLastDayOfMonth, getSelectedDateRangeString } from '@common/utils';
import { DiseasesSection } from './CDCTier1ResultsBreakdown/DiseasesSection';
import { useGoalsStore } from '@modules/study-insights/store';
import { COMMON_TRANSLATION } from '@common/constants';
import { usePermissions } from '@modules/permissions/hooks';
import { Permissions } from '@modules/permissions/types';
import { SubPageHeader } from './SubPageHeader';

const ResultsTracking: FC = () => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [endDate, setEndDate] = useState<Date | undefined>(getLastDayOfMonth());
  const [startDate, setStartDate] = useState<Date | undefined>(getFirstDayOfMonth());
  const endDateGoal = useGoalsStore((store) => store.lifetimeGoal?.endDate);
  const startDateGoal = useGoalsStore((store) => store.lifetimeGoal?.startDate);
  const { hasPermission } = usePermissions();

  const getSelectedData = (): string => {
    if (startDate === undefined || endDate === undefined) {
      return t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION });
    }

    return getSelectedDateRangeString(startDate, endDate);
  };

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
  };

  const {
    data: resultsBreakdownData,
    isLoading: isresultsBreakdownLoading,
    isError: isResultsBreakdownError,
  } = useQueryCDCTier1ResultsBreakdown({
    endDate,
    startDate,
  });

  const isCDCTier1BreakdownEnabled = hasPermission(Permissions.CDC_TIER1_METRICS);
  return (
    <>
      {isCDCTier1BreakdownEnabled && (
        <SubPage
          header={
            <SubPageHeader
              t={t}
              startDateGoal={startDateGoal}
              endDateGoal={endDateGoal}
              handleSelectDateRange={handleSelectDateRange}
              selectedDataLabel={getSelectedData()}
            />
          }
        >
          <Section
            title={isresultsBreakdownLoading ? <Loader css={{ width: '400px' }} /> : t('cdc_tier_1_patients_breakdown')}
          >
            {isresultsBreakdownLoading && <Loader css={{ height: '520px' }} />}
            {!isresultsBreakdownLoading && (
              <DiseasesSection
                data={
                  resultsBreakdownData ?? {
                    delivered: {
                      total: 0,
                      negative: 0,
                      positive: 0,
                      positivePercentage: 0,
                      negativePercentage: 0,
                    },
                    ls: {
                      total: 0,
                      totalPercentage: 0,
                      percentage: 0,
                    },
                    fh: {
                      total: 0,
                      totalPercentage: 0,
                      percentage: 0,
                    },
                    hboc: {
                      total: 0,
                      totalPercentage: 0,
                      percentage: 0,
                    },
                  }
                }
                isError={isResultsBreakdownError}
              />
            )}
          </Section>
        </SubPage>
      )}
    </>
  );
};

export default ResultsTracking;
